import { useState } from 'react';

import { Portal, usePreventWindowScroll } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { Sentry } from '@/components/Sentry';

import { useIsMobileScreen } from '@/hooks/useMedia';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import ActionSheet, { Background, Sheet } from '@/components/ActionSheet';

import { TransactionForm } from './components/TransactionForm';

const sheetWidth = 700;

type Props = {
  holding?: Holding;
  portfolioId: Portfolio['id'];
  onClose: () => void;
};

export const AddTransaction = ({ onClose, ...rest }: Props) => {
  usePreventWindowScroll();

  const [open, setOpen] = useState(true);

  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  return (
    <Sentry
      sentryCapture={{
        team: 'wombats',
      }}
      globalDisabled
    >
      {isMobileScreen ? (
        <Portal>
          <div
            className="fixed left-0 top-0 z-[9999] size-full bg-legacy-noir"
            data-testid="add-transaction-modal"
          >
            <div className="pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]">
              <div className="grid h-[calc(100svh-env(safe-area-inset-top)-env(safe-area-inset-bottom))] grid-rows-[1fr]">
                <TransactionForm onClose={onClose} {...rest} />
              </div>
            </div>
          </div>
        </Portal>
      ) : (
        <ActionSheet
          open={open}
          onClose={() => setOpen(false)}
          onClosed={onClose}
        >
          <Background />
          <Sheet
            sheetLayout={{
              'max-width': `${sheetWidth}px`,
              left: `max(0px, calc(50vw - ${sheetWidth / 2}px))`,
            }}
          >
            <div
              className="grid grid-rows-[calc(50px+env(safe-area-inset-top))]"
              data-testid="add-transaction-action-sheet"
            >
              <div className="row-start-2 grid rounded-t-md bg-legacy-noir">
                <TransactionForm onClose={() => setOpen(false)} {...rest} />
              </div>
            </div>
          </Sheet>
        </ActionSheet>
      )}
    </Sentry>
  );
};
